import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { GlowOrb } from "../../components/ui/Fx";

export default function ProfilePage() {
  const { user, userProfile, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);

  if (!user) { navigate("/login"); return null; }

  const handleLogout = async () => {
    setLoggingOut(true);
    await logout();
    navigate("/");
  };

  const profile = userProfile || {};
  const joinDate = profile.createdAt?.toDate
    ? profile.createdAt.toDate().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : "Unknown";

  const stats = [
    { label: "Status", value: isAdmin ? "Admin" : "Member", color: isAdmin ? "#f5c518" : "#4ade80" },
    { label: "Access", value: isAdmin ? "Full Access" : "Approved", color: "#818cf8" },
    { label: "Member Since", value: joinDate, color: "#c9a84c" },
  ];

  const quickLinks = [
    { to: "/insights", label: "Insights", icon: "◈", color: "#818cf8" },
    { to: "/weekly-outlook", label: "Weekly Outlook", icon: "◎", color: "#f5c518" },
    { to: "/trade-ideas", label: "Trade Ideas", icon: "⚡", color: "#4ade80" },
    { to: "/education", label: "Education", icon: "⊕", color: "#f87171" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#050505", paddingTop: 100, paddingBottom: 80, position: "relative", overflow: "hidden" }}>
      <style>{`
        @keyframes glowOrb { 0%,100%{opacity:.35} 50%{opacity:.8} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes lineIn { from{transform:scaleX(0)} to{transform:scaleX(1)} }
        @keyframes rotateRing { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes pulse { 0%,100%{box-shadow:0 0 0 0 #4ade8055} 60%{box-shadow:0 0 0 10px #4ade8000} }
      `}</style>

      <GlowOrb color="#c9a84c" size={600} top="-10%" right="-5%" opacity={0.05} />
      <GlowOrb color="#818cf8" size={400} bottom="10%" left="-5%" opacity={0.04} />

      <div className="container" style={{ maxWidth: 800, position: "relative", zIndex: 1 }}>

        {/* Header */}
        <div style={{ marginBottom: 48, animation: "fadeUp 0.5s ease both" }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.22em", textTransform: "uppercase", color: "#c9a84c", marginBottom: 12 }}>Profile</div>
          <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
            {/* Avatar */}
            <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#0a0a0a", border: "1px solid #c9a84c30", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 30px #c9a84c15", position: "relative", flexShrink: 0 }}>
              <div style={{ position: "absolute", inset: -4, borderRadius: "50%", border: "1px solid #c9a84c15", animation: "rotateRing 8s linear infinite" }} />
              <span style={{ fontFamily: "'Barlow',sans-serif", fontWeight: 700, fontSize: "1.8rem", color: "#f5c518", textTransform: "uppercase" }}>
                {(profile.name || user.email || "?")[0].toUpperCase()}
              </span>
            </div>
            <div>
              <h1 style={{ fontFamily: "'Barlow',sans-serif", fontWeight: 700, fontSize: "clamp(1.6rem,4vw,2.4rem)", textTransform: "uppercase", color: "#f0f0f0", lineHeight: 1, marginBottom: 6 }}>
                {profile.name || "Trader"}
              </h1>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "#444" }}>{user.email}</div>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 36, animation: "fadeUp 0.5s 0.08s ease both" }} className="stats-grid">
          {stats.map((s, i) => (
            <div key={s.label} style={{ background: "#0a0a0a", border: "1px solid #1e1e1e", borderRadius: "10px", padding: "20px 22px", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,${s.color},transparent)`, animation: `lineIn 0.8s ${i * 0.1}s both` }} />
              <div style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#333", marginBottom: 8 }}>{s.label}</div>
              <div style={{ fontFamily: "'Barlow',sans-serif", fontWeight: 700, fontSize: "1rem", color: s.color, textShadow: `0 0 16px ${s.color}40` }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Access section */}
        <div style={{ background: "#0a0a0a", border: "1px solid #1e1e1e", borderRadius: "12px", padding: "28px 28px", marginBottom: 24, animation: "fadeUp 0.5s 0.14s ease both", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: "linear-gradient(90deg,#4ade80,transparent)" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#4ade80", boxShadow: "0 0 10px #4ade80", animation: "pulse 2s ease-in-out infinite" }} />
            <span style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.18em", textTransform: "uppercase", color: "#4ade80" }}>Research Access</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 10 }} className="links-grid">
            {quickLinks.map((l, i) => (
              <a key={l.to} href={l.to} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: "#080808", border: "1px solid #141414", borderRadius: "8px", textDecoration: "none", transition: "all 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = l.color + "40"; e.currentTarget.style.background = "#0c0c0c"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "#141414"; e.currentTarget.style.background = "#080808"; }}
              >
                <span style={{ fontSize: "1.1rem", color: l.color, filter: `drop-shadow(0 0 6px ${l.color}80)` }}>{l.icon}</span>
                <span style={{ fontFamily: "'Barlow',sans-serif", fontWeight: 600, fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.04em", color: "#888" }}>{l.label}</span>
              </a>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 12, animation: "fadeUp 0.5s 0.2s ease both" }}>
          {isAdmin && (
            <a href="/admin" style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#f5c518", textDecoration: "none", padding: "11px 22px", background: "#f5c51812", border: "1px solid #f5c51830", borderRadius: "6px", transition: "all 0.2s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "#f5c51820"; e.currentTarget.style.boxShadow = "0 0 16px #f5c51820"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#f5c51812"; e.currentTarget.style.boxShadow = "none"; }}
            >Admin Panel</a>
          )}
          <button onClick={handleLogout} disabled={loggingOut}
            style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#f87171", background: "#f8717110", border: "1px solid #f8717128", borderRadius: "6px", padding: "11px 22px", cursor: "pointer", transition: "all 0.2s" }}
            onMouseEnter={e => { e.currentTarget.style.background = "#f8717120"; e.currentTarget.style.boxShadow = "0 0 16px #f8717115"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#f8717110"; e.currentTarget.style.boxShadow = "none"; }}
          >{loggingOut ? "Logging out..." : "Logout"}</button>
        </div>
      </div>
      <style>{`
        @media(max-width:600px){
          .stats-grid{grid-template-columns:1fr!important}
          .links-grid{grid-template-columns:1fr!important}
        }
      `}</style>
    </div>
  );
}
